import Button from "@mui/material/Button";
import { Checkbox } from "components/Form/components/Checkbox/Checkbox";
import { CheckboxGroup } from "components/Form/components/Checkbox/CheckboxGroup";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { Popover } from "components/Popover/Popover";
import { SetStateAction, useCallback, useEffect, useState } from "react";
import "./TableColumnsSelector.scss";
import {
    TableColumn,
    getInitialTableColumns,
    resetLocalStorageTableColumns,
    updateLocalStorageTableSettings,
} from "./tableUtils";

type TableColumnsSelectorProps = {
    initialColumns: TableColumn[];
    setTableColumns: (value: SetStateAction<TableColumn[]>) => void;
    tableSettingsKey: string;
};

const TableColumnsSelector = ({
    initialColumns,
    setTableColumns,
    tableSettingsKey,
}: TableColumnsSelectorProps) => {
    const [selectorColumns, setSelectorColumns] = useState<TableColumn[]>([]);
    const setColumns = useCallback(
        (newColumns) => {
            setTableColumns(newColumns);
            setSelectorColumns(newColumns);
        },
        [setTableColumns]
    );

    useEffect(() => {
        setSelectorColumns(getInitialTableColumns(initialColumns, tableSettingsKey));
    }, [initialColumns, tableSettingsKey]);

    const updateColumns = useCallback(
        (newCols) => {
            setColumns(newCols);
            updateLocalStorageTableSettings(tableSettingsKey, { columns: newCols });
        },
        [tableSettingsKey, setColumns]
    );
    const setColumnShown = (index: number, shown: boolean) => {
        const newColumns = selectorColumns.map((col, curIndex) => {
            const isSelectedColumn = curIndex === index;
            if (!isSelectedColumn) return col;
            const newCol = Object.assign({}, col);
            newCol.shown = shown;
            return newCol;
        });
        updateColumns(newColumns);
    };

    const selectAll = () => {
        const newColumns = selectorColumns.map((col) => {
            const newCol = Object.assign({}, col);
            newCol.shown = true;
            return newCol;
        });
        updateColumns(newColumns);
    };

    const resetToDefaults = useCallback(() => {
        // Drop the persisted column override, then re-apply the default column
        // set (each column's default `shown`) both to the table and the selector.
        resetLocalStorageTableColumns(tableSettingsKey);
        const defaultColumns = initialColumns.map((col) => Object.assign({}, col));
        setColumns(defaultColumns);
    }, [tableSettingsKey, initialColumns, setColumns]);

    return (
        <div className="tableColumnsSelector">
            <Popover
                trigger={<IconButton icon={IconCollection.viewColumnOutline} />}
                closeOnInsideClick={false}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                contentClassName="table-columns__popover"
            >
                <CheckboxGroup ariaLabel="Select columns">
                    <>
                        {selectorColumns.map((column, index) => (
                            <Checkbox
                                key={index}
                                onChange={(event) =>
                                    setColumnShown(index, event.currentTarget.checked)
                                }
                                value={column.shown}
                                label={column.label}
                            />
                        ))}
                        <Button onClick={selectAll}>Show All</Button>
                        <Button onClick={resetToDefaults}>Reset to defaults</Button>
                    </>
                </CheckboxGroup>
            </Popover>
        </div>
    );
};

export default TableColumnsSelector;
